import { useState } from 'react';
import { Organizations, type Role, type Sent } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Dialog, Field, Form } from '@beyond-js/ui/react';
import { Problem } from '@accounts/ui';

const roles: Role[] = ['owner', 'admin', 'developer', 'viewer'];

/**
 * The dialog that invites people to an organization (ARR-03, ARR-12, ACC-16): one or several addresses
 * at once, and the role chosen with what it means. Each address is mailed its link; the results say
 * which were sent and give the link of any that was not. The product that sent the person here, when one
 * did, is named in the message.
 *
 * While the invitations are being sent the dialog cannot be dismissed, and that state always ends: the
 * transport bounds every request. A refusal is explained inside the dialog, where the person is looking.
 * While no address is written Invite member waits quietly and a press puts the caret in the field (D183);
 * what is written and is not an address is said there, and so is more than the 20 the service takes at once.
 */
export function Invite({
	organization,
	owner,
	open,
	onClose,
	onCreated,
	onUnknown
}: {
	organization: string;
	/** Whether the person inviting is an owner, who alone may invite another owner */
	owner: boolean;
	open: boolean;
	onClose: () => void;
	onCreated: (sent: Sent[]) => Promise<unknown>;
	onUnknown: () => Promise<unknown>;
}) {
	const { text, organizations, language, sender } = usePresence();
	const [emails, setEmails] = useState('');
	const [role, setRole] = useState<Role>('developer');
	const [busy, setBusy] = useState(false);
	const [invalid, setInvalid] = useState<string>();
	const [failure, setFailure] = useState<unknown>();

	function close() {
		setInvalid(undefined);
		setFailure(undefined);
		onClose();
	}

	// Something is written here: an empty field stopped the submission in `Form`, saying nothing
	async function invite() {
		const list = [...new Set(emails.split(/[\s,;]+/).map(value => value.trim().toLowerCase()).filter(Boolean))];
		const wrong = list.filter(value => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value));
		// Text that holds no address or a wrong one is said first; then more than the service takes at once
		const problem = !list.length || wrong.length ? text('error.field.email') : list.length > Organizations.LIMIT ? text('invitations.emails.many', { max: Organizations.LIMIT }) : undefined;
		if (problem) {
			setInvalid(problem);
			return;
		}
		setBusy(true);
		setInvalid(undefined);
		setFailure(undefined);
		try {
			const sent = await organizations.send(organization, list, role, sender?.product);
			setEmails('');
			// Each link is shown once and never stored: the service keeps only a digest
			await onCreated(sent);
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return;
			setFailure(error);
			if (Refusal.is(error) && error.unknown) await onUnknown();
		} finally {
			setBusy(false);
		}
	}

	return (
		<Dialog open={open} title={text('invitations.send.title')} busy={busy} labels={Dialog.labels[language]} onClose={close}>
			<Form
				className="flex flex-col gap-4"
				onSubmit={event => {
					event.preventDefault();
					void invite();
				}}
			>
				<Field label={text('invitations.emails')} hint={text('invitations.emails.help')} error={invalid}>
					<textarea rows={3} required value={emails} onChange={event => setEmails(event.target.value)} />
				</Field>
				<fieldset className="flex flex-col gap-2">
					<legend className="mb-1 text-body text-ink">{text('invitations.role')}</legend>
					{roles
						.filter(name => name !== 'owner' || owner)
						.map(name => (
							<label key={name} className="flex cursor-pointer gap-3">
								<input type="radio" name="role" className="mt-1" value={name} checked={role === name} onChange={() => setRole(name)} />
								<span>
									<span className="block text-body text-ink">{text(`role.${name}`)}</span>
									<span className="block text-small text-ink-muted">{text(`role.help.${name}.long`)}</span>
								</span>
							</label>
						))}
				</fieldset>
				{failure ? <Problem error={failure} /> : null}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="quiet" onClick={close}>
						{text('action.cancel')}
					</Button>
					<Button type="submit" variant="primary" busy={busy} labels={{ busy: text('action.saving') }}>
						{text('invitations.send')}
					</Button>
				</div>
			</Form>
		</Dialog>
	);
}
